import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useSelector } from "react-redux";
import { Row, Col, Form, Button, InputGroup, Badge } from "react-bootstrap";
import Select from "react-select";
import { Droppable, Draggable } from "react-beautiful-dnd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTrash, faPen, faCheck, faTimes, faGripVertical, faPlus,
} from "@fortawesome/free-solid-svg-icons";
import CurrencyComponent from "../CurrencyComponent";
import {
  normalizeLayoutSegments,
  patchItemWithLayoutSegments,
  addTextSegmentToItem,
  SEGMENT_TEXT,
  SEGMENT_COMPOSITION,
} from "helper/briqueLayoutSegments";
import { forfaitSegmentsDroppableId } from "helper/briqueDragDropIds";
import { resolvePackageForDeliveryDate } from "helper/packageRotationHelper";

/** Même logique que ProductPackagesComponent.getPackagePiecesBreakdown */
const getPackagePiecesBreakdown = (selectedPackage, products = [], productParts = []) => {
  const counts = { sweet: 0, salty: 0 };

  const computeFromCollection = (collection = [], referenceList = [], referenceKey = "product_id") => {
    collection.forEach((item) => {
      const quantity = Number(item.quantity) || 0;
      if (!quantity) return;

      const reference = referenceList.find((refItem) => refItem.id === item[referenceKey]);
      const sweetFlag =
        typeof reference?.sweet === "boolean"
          ? reference.sweet
          : typeof item?.sweet === "boolean"
            ? item.sweet
            : false;

      if (sweetFlag) counts.sweet += quantity;
      else counts.salty += quantity;
    });
  };

  if (selectedPackage?.products) {
    computeFromCollection(selectedPackage.products, products, "product_id");
  }
  if (selectedPackage?.product_parts) {
    computeFromCollection(
      selectedPackage.product_parts,
      productParts,
      "product_part_id"
    );
  }

  return { ...counts, total: counts.sweet + counts.salty };
};

const lineValueFromRow = (kind, row) => {
  if (kind === "product" && row.product_id) return `product:${row.product_id}`;
  if (kind === "part" && row.product_part_id) return `part:${row.product_part_id}`;
  return null;
};

/**
 * Item forfait en mode brique : même modèle de données que ProductPackagesComponent.
 * — Une liste d’articles : select produits + pièces fusionnés, ajout à la sélection dans la liste.
 * — Si la brique a la répartition salé/sucré : affichage en deux colonnes Salé / Sucré.
 */
const BriqueItemForfait = ({
  item,
  briqueDevisId,
  packages      = [],
  products      = [],
  productParts  = [],
  onUpdate,
  onRemove,
  dragHandleProps = {},
}) => {
  const currentOrderIndex = useSelector((s) => s.orderEdition.currentOrderIndex);
  const orderPerson = useSelector((s) => {
    const o = currentOrderIndex != null ? s.orderEdition.orders?.[currentOrderIndex] : null;
    return Number(o?.deliveryInfo?.person) || 1;
  });
  const orderDeliveryDate = useSelector((s) => {
    const o = currentOrderIndex != null ? s.orderEdition.orders?.[currentOrderIndex] : null;
    return o?.deliveryInfo?.deliveryDate || null;
  });
  const rotationSlots = useSelector((s) => s.rotationSlots?.data || []);

  const [isEditingName, setIsEditingName] = useState(false);
  // Précédence du nom affiché : rename manuel > nom AO renommé (renammed) > nom de base du package.
  // (Aligne l'éditeur brique sur le recap panier et le PDF, qui priorisent le nom AO.)
  const [nameValue, setNameValue]         = useState(
    item.name_override || item.renammed || item.effective_name || ""
  );
  const [tempQuantity, setTempQuantity]   = useState({});

  const linkedPackage = packages.find((p) => p.id === item.package_id) || null;
  const lineProducts = item.products || [];
  const lineParts    = item.product_parts || [];
  const hasSaltySweetSplit =
    item.has_salty_sweet_split === true || item.hasSaltySweetSplit === true;

  const hydrateRef = useRef(false);

  useEffect(() => {
    setNameValue(item.name_override || item.renammed || item.effective_name || "");
  }, [item.name_override, item.effective_name, item.renammed]);

  // Anciens JSON sans composition : hydrater depuis le package + extra_articles
  useEffect(() => {
    if (hydrateRef.current) return;
    if (!linkedPackage) return;
    if (Array.isArray(item.products) || Array.isArray(item.product_parts)) {
      hydrateRef.current = true;
      return;
    }
    hydrateRef.current = true;
    const nb = Number(item.nb_persons) || orderPerson || 1;
    const pkgN = linkedPackage.participants_number || 1;
    // Composition résolue pour la date de livraison (slots de rotation)
    const resolvedPackage = resolvePackageForDeliveryDate(linkedPackage, orderDeliveryDate, rotationSlots);
    const mergedProducts = (resolvedPackage.products || []).map((product) => ({
      quantity: Math.ceil(product.quantity * (nb / pkgN)),
      product_id: product.product_id,
      sweet: product.sweet,
    }));
    const mergedParts = (resolvedPackage.product_parts || []).map((productPart) => ({
      quantity: Math.ceil(productPart.quantity * (nb / pkgN)),
      product_part_id: productPart.product_part_id,
      sweet: productPart.sweet,
    }));
    (item.extra_articles || []).forEach((a) => {
      const q = Number(a.qty) || 0;
      if (!q) return;
      if (a.source_type === "product") {
        mergedProducts.push({ quantity: q, product_id: a.source_id, sweet: !!a.sweet });
      } else if (a.source_type === "part") {
        mergedParts.push({ quantity: q, product_part_id: a.source_id, sweet: !!a.sweet });
      }
    });
    onUpdate({
      ...item,
      nb_persons: item.nb_persons ?? nb,
      products: mergedProducts,
      product_parts: mergedParts,
      extra_articles: [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedPackage?.id, item.package_id, item._localId]);

  const selectedPackageShape = useMemo(
    () => ({ products: lineProducts, product_parts: lineParts }),
    [lineProducts, lineParts]
  );

  const mergedCatalogOptions = useMemo(() => {
    const opts = [
      ...products.map((p) => ({
        value: `product:${p.id}`,
        label: p.name,
        meta:  { kind: "product", id: p.id, sweet: !!p.sweet },
      })),
      ...productParts.map((pp) => ({
        value: `part:${pp.id}`,
        label: `${pp.name} (pièce)`,
        meta:  { kind: "part", id: pp.id, sweet: !!pp.sweet },
      })),
    ];
    return opts.sort((a, b) =>
      a.label.localeCompare(b.label, "fr", { sensitivity: "base" })
    );
  }, [products, productParts]);

  const lineDescriptors = useMemo(() => {
    const p = lineProducts.map((row, idx) => ({ kind: "product", idx, row }));
    const t = lineParts.map((row, idx) => ({ kind: "part", idx, row }));
    const all = [...p, ...t];
    return {
      productsOnly: p,
      partsOnly:    t,
      all,
      salty:        all.filter(({ row }) => !row.sweet),
      sweet:        all.filter(({ row }) =>  row.sweet),
    };
  }, [lineProducts, lineParts]);

  const handleNameSave = () => {
    onUpdate({ ...item, name_override: nameValue });
    setIsEditingName(false);
  };

  const patchItem = (partial) => onUpdate({ ...item, ...partial });

  const updateProductPackageProducts = (productIndex, productId, type) => {
    const next = type === "product" ? [...lineProducts] : [...lineParts];
    if (type === "product") {
      const meta = products.find((p) => p.id === productId);
      next[productIndex] = {
        ...next[productIndex],
        product_id: productId,
        sweet: typeof meta?.sweet === "boolean" ? meta.sweet : next[productIndex].sweet,
      };
    } else {
      const meta = productParts.find((p) => p.id === productId);
      next[productIndex] = {
        ...next[productIndex],
        product_part_id: productId,
        sweet: typeof meta?.sweet === "boolean" ? meta.sweet : next[productIndex].sweet,
      };
    }
    patchItem(type === "product" ? { products: next } : { product_parts: next });
  };

  const handleMergedSelectChange = (option, fromKind, fromIndex) => {
    if (!option?.meta) return;
    const { kind, id, sweet } = option.meta;
    const sourceRow =
      fromKind === "product" ? lineProducts[fromIndex] : lineParts[fromIndex];
    const qty = sourceRow?.quantity ?? 1;

    if (fromKind === kind) {
      if (kind === "product") updateProductPackageProducts(fromIndex, id, "product");
      else updateProductPackageProducts(fromIndex, id, "productPart");
      return;
    }

    if (fromKind === "product") {
      const newProducts = lineProducts.filter((_, i) => i !== fromIndex);
      const newParts = [
        ...lineParts,
        {
          quantity:        qty,
          product_part_id: id,
          sweet:           typeof sweet === "boolean" ? sweet : !!sweet,
        },
      ];
      patchItem({ products: newProducts, product_parts: newParts });
    } else {
      const newParts = lineParts.filter((_, i) => i !== fromIndex);
      const newProducts = [
        ...lineProducts,
        {
          quantity:   qty,
          product_id: id,
          sweet:      typeof sweet === "boolean" ? sweet : !!sweet,
        },
      ];
      patchItem({ products: newProducts, product_parts: newParts });
    }
  };

  const updateProductPackageProductsQuantity = (kind, productIndex, quantity) => {
    if (kind === "product") {
      const next = [...lineProducts];
      next[productIndex] = { ...next[productIndex], quantity: Number(quantity) };
      patchItem({ products: next });
    } else {
      const next = [...lineParts];
      next[productIndex] = { ...next[productIndex], quantity: Number(quantity) };
      patchItem({ product_parts: next });
    }
  };

  const removeProductPackageElement = (kind, productIndex) => {
    if (kind === "product") {
      patchItem({ products: lineProducts.filter((_, i) => i !== productIndex) });
    } else {
      patchItem({ product_parts: lineParts.filter((_, i) => i !== productIndex) });
    }
  };

  const handleNewLineSelectChange = (option) => {
    if (!option?.meta) return;
    const { kind, id, sweet } = option.meta;
    if (kind === "product") {
      patchItem({
        products: [
          ...lineProducts,
          { quantity: 1, product_id: id, sweet: !!sweet },
        ],
      });
    } else {
      patchItem({
        product_parts: [
          ...lineParts,
          { quantity: 1, product_part_id: id, sweet: !!sweet },
        ],
      });
    }
  };

  const toggleRowSweet = (kind, idx) => {
    if (kind === "product") {
      const next = [...lineProducts];
      next[idx] = { ...next[idx], sweet: !next[idx].sweet };
      patchItem({ products: next });
    } else {
      const next = [...lineParts];
      next[idx] = { ...next[idx], sweet: !next[idx].sweet };
      patchItem({ product_parts: next });
    }
  };

  const updateProductPackageQuantity = (newQuantity) => {
    if (newQuantity < 1) return;
    let raw = newQuantity;
    if (typeof raw === "string") {
      raw = raw.replace(",", ".");
    }
    const newPackageQuantity = Number(raw);
    if (!Number.isFinite(newPackageQuantity) || newPackageQuantity < 1) return;

    const oldQ = Number(item.nb_persons);
    const oldPackageQuantity = oldQ >= 1 ? oldQ : 1;
    const selectedPackageInOffer = linkedPackage;
    const packageInitialQuantity = selectedPackageInOffer
      ? selectedPackageInOffer.participants_number
      : null;

    const nextProducts = lineProducts.map((product) => {
      let initialProductQuantity = null;
      const initialProductInPackage =
        selectedPackageInOffer?.products?.find((p) => p.product_id === product.product_id);
      if (initialProductInPackage) {
        initialProductQuantity = initialProductInPackage.quantity;
      }
      let productQuantity = (product.quantity * newPackageQuantity) / oldPackageQuantity;
      if (packageInitialQuantity && initialProductQuantity) {
        productQuantity = (initialProductQuantity * newPackageQuantity) / packageInitialQuantity;
      }
      return { ...product, quantity: Math.ceil(productQuantity) };
    });

    const nextParts = lineParts.map((productPart) => {
      let initialPartQuantity = null;
      const initialPartInPackage =
        selectedPackageInOffer?.product_parts?.find(
          (p) => p.product_part_id === productPart.product_part_id
        );
      if (initialPartInPackage) {
        initialPartQuantity = initialPartInPackage.quantity;
      }
      let partQuantity = (productPart.quantity * newPackageQuantity) / oldPackageQuantity;
      if (packageInitialQuantity && initialPartQuantity) {
        partQuantity = (initialPartQuantity * newPackageQuantity) / packageInitialQuantity;
      }
      return { ...productPart, quantity: Math.ceil(partQuantity) };
    });

    patchItem({
      nb_persons: newPackageQuantity,
      products: nextProducts,
      product_parts: nextParts,
    });
  };

  const handleConfirmQuantity = () => {
    updateProductPackageQuantity(tempQuantity._pending ?? item.nb_persons);
    setTempQuantity({});
  };

  const handleQuantityFieldChange = (val) => {
    setTempQuantity({ _pending: val });
  };

  const displayNb = tempQuantity._pending ?? item.nb_persons ?? "";

  const pieceCounts = getPackagePiecesBreakdown(selectedPackageShape, products, productParts);

  const canAddCatalogLine = products.length > 0 || productParts.length > 0;

  const layoutSegments = useMemo(() => normalizeLayoutSegments(item), [
    item.layout_segments,
    item.text,
    item._localId,
  ]);
  const layoutDndType = `LAY_FORFAIT_${item._localId}`;
  const segmentsDroppableId = forfaitSegmentsDroppableId(briqueDevisId, item._localId);

  const updateSegmentText = useCallback(
    (segId, text) => {
      const next = layoutSegments.map((s) =>
        s._localId === segId ? { ...s, text } : s
      );
      onUpdate(patchItemWithLayoutSegments(item, next));
    },
    [item, layoutSegments, onUpdate]
  );

  const removeTextSegment = useCallback(
    (segId) => {
      const next = layoutSegments.filter(
        (s) => s.type === SEGMENT_COMPOSITION || s._localId !== segId
      );
      onUpdate(patchItemWithLayoutSegments(item, next));
    },
    [item, layoutSegments, onUpdate]
  );

  const handleAddTextSegment = useCallback(() => {
    onUpdate(addTextSegmentToItem(item, layoutSegments.length));
  }, [item, layoutSegments.length, onUpdate]);

  const renderArticleRow = ({ kind, idx, row }) => {
    const lineVal = lineValueFromRow(kind, row);
    const selectValue = lineVal
      ? mergedCatalogOptions.find((o) => o.value === lineVal) || null
      : null;

    return (
      <Row
        key={`${kind}-${idx}-${row.product_id || row.product_part_id || "x"}`}
        className="align-items-center mb-2"
        style={{ margin: "6px 0" }}
      >
        <Col md={3}>
          <Form.Control
            type="number"
            size="sm"
            className="form-control"
            value={row.quantity}
            step="0.01"
            onChange={(e) =>
              updateProductPackageProductsQuantity(kind, idx, e.target.value)
            }
          />
        </Col>
        <Col md={hasSaltySweetSplit ? 7 : 8}>
          {lineVal ? (
            <Select
              options={mergedCatalogOptions}
              closeMenuOnSelect
              onChange={(opt) => handleMergedSelectChange(opt, kind, idx)}
              value={selectValue}
              placeholder="Choisir…"
            />
          ) : (
            <Form.Control type="text" size="sm" value="(élément supprimé)" readOnly />
          )}
        </Col>
        <Col md={1}>
          <Button
            size="sm"
            variant="outline-danger"
            onClick={() => removeProductPackageElement(kind, idx)}
          >
            <FontAwesomeIcon icon={faTimes} />
          </Button>
        </Col>
      </Row>
    );
  };

  const renderArticlesBlock = () => {
    if (hasSaltySweetSplit) {
      return (
        <>
          {(lineDescriptors.salty.length > 0) && (
            <div className="mb-3">
              <small className="text-muted d-block mb-2" style={{ fontWeight: 600 }}>
                Salé
                <Badge variant="secondary" className="ml-2" style={{ fontSize: "0.65rem" }}>
                  {lineDescriptors.salty.length} ligne{lineDescriptors.salty.length > 1 ? "s" : ""}
                </Badge>
              </small>
              {lineDescriptors.salty.map(renderArticleRow)}
            </div>
          )}
          {(lineDescriptors.sweet.length > 0) && (
            <div className="mb-3">
              <small className="text-muted d-block mb-2" style={{ fontWeight: 600 }}>
                Sucré
                <Badge variant="secondary" className="ml-2" style={{ fontSize: "0.65rem" }}>
                  {lineDescriptors.sweet.length} ligne{lineDescriptors.sweet.length > 1 ? "s" : ""}
                </Badge>
              </small>
              {lineDescriptors.sweet.map(renderArticleRow)}
            </div>
          )}
          {lineDescriptors.all.length === 0 && (
            <p className="small text-muted mb-2">Aucune ligne — ajoutez un article ci-dessous.</p>
          )}
        </>
      );
    }

    return <>{lineDescriptors.all.map(renderArticleRow)}</>;
  };

  const renderAddRow = () => (
    <Row className="align-items-end mb-2 mt-1" style={{ margin: "6px 0" }}>
      <Col md={12}>
        <Form.Label className="small mb-1 text-muted">Ajouter un article (produit ou pièce)</Form.Label>
        <Select
          placeholder="Chercher un produit ou une pièce…"
          options={mergedCatalogOptions}
          value={null}
          onChange={handleNewLineSelectChange}
          noOptionsMessage={() => "Aucun résultat"}
        />
      </Col>
    </Row>
  );

  return (
    <div className="brique-item brique-item--forfait mb-3 border rounded p-0 bg-white">

      <Row className="align-items-center mx-0 border-bottom py-2 px-3 bg-light flex-nowrap" style={{ flexWrap: "wrap" }}>
        <Col xs="auto" className="pl-0 pr-2 text-muted" {...dragHandleProps} style={{ cursor: "grab" }}>
          <FontAwesomeIcon icon={faGripVertical} />
        </Col>

        <Col md={4} className="py-1">
          {isEditingName ? (
            <div className="d-flex align-items-center" style={{ gap: "0.25rem" }}>
              <Form.Control
                size="sm"
                value={nameValue}
                onChange={(e) => setNameValue(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleNameSave()}
                autoFocus
              />
              <Button size="sm" variant="success" onClick={handleNameSave}>
                <FontAwesomeIcon icon={faCheck} />
              </Button>
              <Button size="sm" variant="outline-secondary" onClick={() => setIsEditingName(false)}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </div>
          ) : (
            <span
              style={{ cursor: "pointer", fontWeight: 600 }}
              onClick={() => setIsEditingName(true)}
              title="Cliquer pour modifier le nom"
            >
              {nameValue || item.effective_name || "Forfait sans nom"}
              <FontAwesomeIcon icon={faPen} className="ml-2 text-muted small" style={{ fontSize: "0.75rem" }} />
            </span>
          )}
        </Col>

        <Col md={3} className="py-1">
          <Form.Group className="mb-0">
            <CurrencyComponent
              initialPrice={item.price_per_person}
              onPriceChange={(val) => patchItem({ price_per_person: val })}
              nullable
              placeholder="Prix par personne"
            />
          </Form.Group>
        </Col>

        <Col md={2} className="py-1">
          <Form.Group controlId={`brique-forfait-nb-${item._localId}`} className="mb-0">
            <InputGroup size="sm">
              <Form.Control
                type="number"
                value={displayNb}
                onChange={(e) => handleQuantityFieldChange(e.target.value)}
                className="form-control"
                step="0.01"
                placeholder="Nb pers"
                min="1"
              />
              {Object.keys(tempQuantity).length > 0 && (
                <InputGroup.Append>
                  <Button variant="primary" size="sm" onClick={handleConfirmQuantity}>
                    Recalculer
                  </Button>
                </InputGroup.Append>
              )}
            </InputGroup>
          </Form.Group>
        </Col>

        {item.price_per_person != null && item.nb_persons != null && (
          <Col md="auto" className="py-1">
            <b style={{ fontSize: "0.9rem", whiteSpace: "nowrap" }}>
              {((item.price_per_person * item.nb_persons) / 100).toFixed(2)} €
            </b>
          </Col>
        )}

        <Col xs="1" className="ml-auto py-1">
          <Button size="sm" variant="outline-danger" onClick={onRemove} title="Supprimer cet item">
            <FontAwesomeIcon icon={faTrash} />
          </Button>
        </Col>
      </Row>

      <div className="px-3 py-2">
        {Object.keys(tempQuantity).length === 0 ? (
          <Droppable droppableId={segmentsDroppableId} type={layoutDndType}>
            {(dropProvided) => (
              <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                {layoutSegments.map((seg, segIndex) => (
                  <Draggable
                    key={seg._localId}
                    draggableId={`forfait-seg-${item._localId}-${seg._localId}`}
                    index={segIndex}
                    type={layoutDndType}
                  >
                    {(dragProvided) => (
                      <div
                        ref={dragProvided.innerRef}
                        {...dragProvided.draggableProps}
                        className="mb-2"
                      >
                        {seg.type === SEGMENT_COMPOSITION ? (
                          <div className="border rounded p-2 bg-white">
                            <div
                              className="d-flex align-items-center text-muted mb-2"
                              {...dragProvided.dragHandleProps}
                              style={{ cursor: "grab", fontSize: "0.75rem", fontWeight: 600 }}
                            >
                              <FontAwesomeIcon icon={faGripVertical} className="mr-2" />
                            </div>
                            {pieceCounts.total > 0 && (
                              <div
                                className="text-muted text-center"
                                style={{ fontSize: "0.8rem", marginBottom: "0.5rem" }}
                              >
                                {`${pieceCounts.total} pièces : ${pieceCounts.sweet} sucrées / ${pieceCounts.salty} salées`}
                              </div>
                            )}
                            {renderArticlesBlock()}
                            {!canAddCatalogLine ? (
                              <p className="small text-muted mb-0">Aucun produit ni pièce dans le catalogue.</p>
                            ) : (
                              renderAddRow()
                            )}
                          </div>
                        ) : (
                          <div className="border rounded p-2 bg-light">
                            <div className="d-flex align-items-start" style={{ gap: "0.35rem" }}>
                              <span
                                className="text-muted pt-1"
                                {...dragProvided.dragHandleProps}
                                style={{ cursor: "grab", flexShrink: 0 }}
                                title="Glisser pour réordonner"
                              >
                                <FontAwesomeIcon icon={faGripVertical} />
                              </span>
                              <Form.Control
                                as="textarea"
                                rows={2}
                                size="sm"
                                value={seg.text || ""}
                                onChange={(e) => updateSegmentText(seg._localId, e.target.value)}
                                placeholder="Texte (devis / facture) — placez ce bloc avant ou après la composition"
                                style={{ resize: "vertical" }}
                              />
                              <Button
                                size="sm"
                                variant="outline-danger"
                                className="flex-shrink-0"
                                onClick={() => removeTextSegment(seg._localId)}
                                title="Supprimer ce texte"
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </Draggable>
                ))}
                {dropProvided.placeholder}
              </div>
            )}
          </Droppable>
        ) : (
          <p className="small text-danger text-center mb-0">
            Validez le nouveau nombre de personnes avec « Recalculer » pour mettre à jour les quantités (comme en mode classique).
          </p>
        )}
      </div>

      <div className="px-3 pb-2 d-flex" style={{ gap: "0.5rem" }}>
        {Object.keys(tempQuantity).length === 0 && (
          <Button size="sm" variant="outline-secondary" onClick={handleAddTextSegment}>
            <FontAwesomeIcon icon={faPlus} className="mr-1" />
            Texte
          </Button>
        )}
      </div>
    </div>
  );
};

export default BriqueItemForfait;
